import React, { useState } from "react";
import { Box, Typography, Stack, Button, useTheme, alpha } from "@mui/material";
import { ACCENT, MONO, MEASURE } from "../data/studioStory";
import pulse from "../data/studioPulse.json";

/**
 * What the studio's agents actually did, on the most recent day we have.
 *
 * Two rules this component exists to hold:
 *
 * 1. It never claims more than happened. Products with no user-facing change
 *    are counted honestly in a footer line rather than padded into the list or
 *    quietly dropped. Twenty invented wins a day would be exactly the
 *    performative thing this is meant not to be.
 * 2. It says how old it is. A stale board is worse than no board, so past a
 *    couple of days it labels itself instead of implying today.
 */

const DAY_MS = 86400000;

function daysOld(dateStr) {
  const then = Date.parse(`${dateStr}T12:00:00Z`);
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / DAY_MS);
}

function formatDay(dateStr) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function StudioPulse({ links = [] }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [expanded, setExpanded] = useState(false);

  // Matched on name, so a rename just drops the link rather than shipping a
  // dead one. Products we don't have a public URL for stay unlinked on purpose.
  const hrefByName = new Map(links.map((l) => [l.name, l.href]));

  // Mirrors bucketEntries() in scripts/pulse-public.mjs. Duplicated rather than
  // imported because that module reaches for node:fs and would not survive the
  // browser build — keep the two predicates in step. A product we never managed
  // to read is not a product that had a quiet day, and the footer must not
  // claim otherwise.
  const shipped = (pulse.entries || []).filter((e) => e.line);
  const quiet = (pulse.entries || []).filter((e) => !e.line && e.observed !== false);
  const unobserved = (pulse.entries || []).filter((e) => !e.line && e.observed === false);
  if (!shipped.length) return null;

  const age = daysOld(pulse.date);
  const stale = age !== null && age >= 2;
  const visible = expanded ? shipped : shipped.slice(0, 6);

  return (
    <Box
      sx={{
        mt: { xs: 5, md: 6 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isDark
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.common.black, 0.015),
        overflow: "hidden",
        textAlign: "left",
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 1.75,
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.2em",
            fontWeight: 700,
            color: "text.secondary",
          }}
        >
          What the studio did
        </Typography>
        <Typography
          sx={{ fontFamily: MONO, fontSize: "0.78rem", color: "text.secondary" }}
        >
          {stale ? `${formatDay(pulse.date)} — last full day on record` : formatDay(pulse.date)}
        </Typography>
      </Stack>

      <Box sx={{ px: { xs: 2.5, md: 3 }, py: 1 }}>
        {visible.map((e) => {
          const href = hrefByName.get(e.name);
          return (
          <Stack
            key={e.id}
            direction={{ xs: "column", sm: "row" }}
            sx={{
              py: 1.5,
              gap: { xs: 0.25, sm: 2 },
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              "&:last-of-type": { borderBottom: "none" },
            }}
          >
            <Typography
              {...(href
                ? { component: "a", href, target: "_blank", rel: "noopener" }
                : {})}
              sx={{
                fontFamily: MONO,
                fontSize: "0.8rem",
                fontWeight: 500,
                // The accent colour is the site's link colour, so it has to mean
                // "you can open this". Every cold evaluator clicked a product
                // name here first and hit dead text — the same trap the app
                // cards fell into (see the comment on the card's `component="a"`).
                color: href ? "accent.text" : "text.secondary",
                textDecoration: "none",
                ...(href ? { "&:hover": { textDecoration: "underline" } } : {}),
                minWidth: { sm: 150 },
                flexShrink: 0,
              }}
            >
              {e.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.6,
                color: "text.primary",
                // Uncapped, these rows ran ~95 characters per line — the widest
                // measure on the page. MEASURE is the shared, calibrated prose cap;
                // see the long note on it in data/studioStory.js for why a
                // hand-typed `ch` value silently misses.
                maxWidth: MEASURE,
              }}
            >
              {e.line}
            </Typography>
          </Stack>
          );
        })}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 1.5,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
          {quiet.length > 0
            ? `${quiet.length} other ${quiet.length === 1 ? "product" : "products"} ran the same daily cycle without a change you'd notice.`
            : "Every product in the portfolio shipped something you'd notice."}
          {unobserved.length > 0 &&
            ` ${unobserved.length} more had not reported yet when this was taken.`}
        </Typography>
        {shipped.length > 6 && (
          <Button
            onClick={() => setExpanded((v) => !v)}
            variant="text"
            size="small"
            sx={{ color: "accent.text", fontWeight: 600, minHeight: 44, px: 1 }}
          >
            {expanded ? "Show less" : `Show all ${shipped.length}`}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export default StudioPulse;
